import React, {useState} from 'react';
import {Search} from 'lucide-react';

import {useAppSelector} from '../state/hooks';
import {useAllStaff} from '../net/query/AllStaff.gql';
import {useUpdateStaff} from '../net/query/UpdateStaff.gql';
import {AccountType, Role, type StaffAccount} from '../net/types/graphql';
import {withValue, withoutValue} from '../util/list';
import {Switch} from '../components/ui/switch';
import {Input} from '../components/ui/input';
import {Spinner} from '../components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

/**
 * View to manage a list of staff users.
 */
export const ManageStaff = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [updateStaff, {loading: updatingStaff}] = useUpdateStaff();
  const {userId, roles} = useAppSelector(({auth}) => ({
    userId: auth.userId!,
    roles: auth.roles || [],
  }));
  const {data, loading, error} = useAllStaff({
    skip: !roles.includes(Role.Admin),
    variables: {
      offset: 0,
      // Load everyone up front, effectively.
      // TODO: API is limited because we can't search by name, since we don't
      // store name directly. So virtualized list would be impossible to sort
      // in a sensible way.
      limit: 10000,
    },
  });

  if (error) {
    return <div className="text-destructive p-4">Error: {error.message}</div>;
  }

  if (!data?.staff && !loading) {
    return <div className="text-destructive p-4">Error: No Data</div>;
  }

  // Filter out any failed accounts (this indicates a mismatch between our database
  // and the upstream org database, usually).
  // Also filter out BOT users which don't need to be managed here.
  // Lastly, sort by name (not last name, for now).
  const allStaff = (data?.staff || [])
    .filter((s: StaffAccount) => !!s.fullName && s.type !== AccountType.Bot)
    .sort((a: StaffAccount, b: StaffAccount) =>
      a.fullName.toLowerCase() < b.fullName.toLowerCase() ? -1 : 1,
    );

  // Filter by search term
  const filteredStaff = allStaff.filter((staff: StaffAccount) => {
    if (!searchTerm) return true;
    const pat = searchTerm
      .replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&')
      .replace(/\s+/g, '\\s+');
    const exp = new RegExp(pat, 'ig');
    return exp.test(staff.fullName);
  });

  // Only show loading state on initial load, not during updates
  if (loading && !data?.staff) {
    return (
      <div className="p-4 text-color-(--muted-foreground)">
        Loading staff...
      </div>
    );
  }

  return (
    <div className="space-y-4 mb-16">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search staff by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Staff Table */}
      <div className="rounded-md border relative">
        {updatingStaff && (
          <div className="absolute inset-0 bg-(--muted)/50 flex items-center justify-center z-10">
            <div className="flex items-center space-x-2">
              <Spinner size="sm" />
              <span className="text-sm text-color-(--muted-foreground)">
                Updating...
              </span>
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Active</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-20">Admin</TableHead>
              <TableHead className="w-20">Staff</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStaff.map((staff: StaffAccount) => (
              <TableRow key={staff.id}>
                <TableCell className="text-center">
                  <Switch
                    disabled={staff.id === userId || updatingStaff}
                    checked={staff.isActive || false}
                    onCheckedChange={(checked) => {
                      updateStaff({
                        variables: {
                          id: staff.id,
                          isActive: checked,
                          roles: staff.roles,
                        },
                      });
                      return false;
                    }}
                  />
                </TableCell>
                <TableCell className="font-medium">{staff.fullName}</TableCell>
                <TableCell className="text-center">
                  <Switch
                    disabled={
                      staff.id === userId || !staff.isActive || updatingStaff
                    }
                    checked={staff.roles.includes(Role.Admin)}
                    onCheckedChange={(checked) => {
                      updateStaff({
                        variables: {
                          id: staff.id,
                          isActive: staff.isActive,
                          roles: checked
                            ? withValue(staff.roles.slice(), Role.Admin)
                            : withoutValue(staff.roles.slice(), Role.Admin),
                        },
                      });
                      return false;
                    }}
                  />
                </TableCell>
                <TableCell className="text-center">
                  <Switch
                    disabled={!staff.isActive || updatingStaff}
                    checked={staff.roles.includes(Role.Staff)}
                    onCheckedChange={(checked) => {
                      updateStaff({
                        variables: {
                          id: staff.id,
                          isActive: staff.isActive,
                          roles: checked
                            ? withValue(staff.roles.slice(), Role.Staff)
                            : withoutValue(staff.roles.slice(), Role.Staff),
                        },
                      });
                      return false;
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredStaff.length === 0 && searchTerm && (
        <div className="text-center text-color-(--muted-foreground) py-8">
          No staff members found matching "{searchTerm}"
        </div>
      )}
    </div>
  );
};
